import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import Chip from "@mui/joy/Chip";
import List from "@mui/joy/List";
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';

import getIcon from "utils/getIcon";
import useIdentity from "hooks/useIdentity";
import useUserIdentity from "hooks/useUserIdentity";

import { Identity } from 'stores/identity_store';
import { SidebarLink } from 'components/Sidebar';
import IdentityAvatar from 'components/IdentityAvatar';
import useRoles from 'hooks/useRoles';

export function SidebarIdentityLink({ identity }: { identity: Identity }) {
    const location = useLocation();
    const url = `/identity/${identity.uuid}`;
    const viewing = location.pathname === url;

    if(!identity) {
        return <em>Loading</em>;
    }
    return (
        <SidebarLink to={url} key={identity.key} >
            <ListItem>
                <ListItemButton
                            variant={viewing ? "soft" : undefined }
                            color={viewing ? "primary" : undefined } >
                    <ListItemContent>
                        <IdentityAvatar
                            size="inherit"
                            level="body-xs"
                            interactive={false}
                            identityKey={identity.uuid} />
                    </ListItemContent>
                </ListItemButton>
            </ListItem>
        </SidebarLink>
    );
}


export const ManagerIdentitiesList = observer(() => {
    const { rolesNames } = useRoles();
    const { identities, fetchAllIdentities } = useIdentity();
    const { currentIdentity } = useUserIdentity();
    const [fetched, setDoFetching] = useState(false);
    const [page, setPage] = useState(1);
    const LabelImportant = getIcon('LabelImportant');
    const PAGE_SIZE = 10;
    const DownIcon = getIcon('ArrowDropDown');
    const doFetch = useCallback(() => {
        if(fetched) return;
        if(!rolesNames.includes('manager')) return;
        setDoFetching(true);
        fetchAllIdentities(currentIdentity.key);
    }, [fetchAllIdentities, fetched, setDoFetching, rolesNames]);

    useEffect(() => {
        doFetch();
    }, [doFetch]);

    const sortedIdentities = identities.slice()
        .sort((a, b) => a.date_created > b.date_created ? -1 : 1);


    return (
        <Stack m={1} spacing={2}>
            <Stack>
            <Typography level="title-lg"
                endDecorator={
                    <Chip
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        slotProps={{ root: { component: 'span' } }}
                    >{sortedIdentities.length}</Chip>
                }
            >
                Identities
            </Typography>

                <Typography
                    color="warning"
                    startDecorator={<LabelImportant />}
                    level="body-xs">
                    Manager Only
                </Typography>
            </Stack>
            <List

            >
                {sortedIdentities.slice(0, page * PAGE_SIZE).map((item) => (
                    <SidebarIdentityLink identity={item} key={item.key} />
                ))}
                {sortedIdentities.length > page * PAGE_SIZE && (
                    <Chip
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        endDecorator={<DownIcon />}
                        slotProps={{ root: { component: 'span' } }}
                    >Show More</Chip>
                )}

            </List>
        </Stack>
    );
});

export default ManagerIdentitiesList;
